import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import { configureTrigger } from '../lib/seal/sealApi';
import {
  decryptOwnerDraft,
  resealOwnerDraft,
  discardOwnerDraft,
} from '../lib/senior/ownerReview';
import { ScheduleView, type MessageDraft } from './ScheduleView';
import './record.css';

type Phase = 'loading' | 'ready' | 'sealing' | 'error';

/**
 * The owner's review of a senior-contributed recording (the Dorothy flow's last
 * step). Opens the queued recording with the owner's device key (the app's first
 * decryption), plays it, and lets the owner seal it to a recipient with a
 * delivery trigger — or discard it. Nothing the senior submitted can be
 * delivered until the owner acts here.
 */
export default function SeniorReviewScreen() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { messageId } = useParams<{ messageId: string }>();

  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [vaultId, setVaultId] = useState<string | null>(null);
  // The plaintext CEK, held from decrypt until re-seal (which zeroizes it).
  const cekRef = useRef<Uint8Array | null>(null);
  // Whether the re-seal already succeeded (so a trigger-config retry doesn't
  // re-seal, and doesn't need a CEK).
  const sealedRef = useRef(false);
  // The recipient the recording was actually SEALED to. Once set, the recipient
  // is cryptographically locked — the CEK is wrapped to them and a retry cannot
  // change it — so the UI must stop pretending it can be changed.
  const [sealedRecipientName, setSealedRecipientName] = useState<string | null>(null);
  const sealedRecipientIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (messageId === undefined || user?.email === undefined) return;
      try {
        const dash = await getMyVault(api);
        const payload = await decryptOwnerDraft({
          api,
          vaultId: dash.vault.id,
          messageId,
          ownerEmail: user.email,
        });
        if (cancelled) {
          payload.cek.fill(0);
          return;
        }
        setVaultId(dash.vault.id);
        cekRef.current = payload.cek;
        setBlob(payload.blob);
        setPhase('ready');
      } catch {
        if (!cancelled) {
          setError('We could not open this recording. It may have been reviewed already.');
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (cekRef.current !== null) {
        cekRef.current.fill(0);
        cekRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, user?.email]);

  const onScheduled = useCallback(
    async (draft: MessageDraft) => {
      if (messageId === undefined || vaultId === null || user?.email === undefined) return;
      setPhase('sealing');
      setError(null);
      try {
        // Idempotent retry (mirrors useSealFlow): if a prior attempt already
        // sealed, skip straight to the trigger (a sealed-without-trigger message
        // would never deliver). Otherwise re-seal — re-decrypting first if a
        // failed prior attempt already zeroized the CEK, so retry never no-ops.
        if (!sealedRef.current) {
          let cek = cekRef.current;
          cekRef.current = null;
          if (cek === null) {
            const payload = await decryptOwnerDraft({ api, vaultId, messageId, ownerEmail: user.email });
            cek = payload.cek; // resealOwnerDraft zeroizes it
          }
          await resealOwnerDraft({ api, vaultId, messageId, cek, recipientId: draft.recipientId });
          sealedRef.current = true;
          sealedRecipientIdRef.current = draft.recipientId;
          setSealedRecipientName(draft.recipientName); // recipient is now locked
        }
        await configureTrigger(api, messageId, {
          condition: draft.condition ?? { kind: 'date', date: draft.deliveryDate },
          triggerTimezone: 'UTC',
        });
        navigate('/recordings', { replace: true });
      } catch {
        setError(
          sealedRef.current
            ? 'The recording was sealed but we could not set its delivery time — tap Continue to try again.'
            : 'We could not seal this recording. Please try again.',
        );
        setPhase('ready');
      }
    },
    [api, vaultId, messageId, user?.email, navigate],
  );

  const onDiscard = useCallback(async () => {
    if (messageId === undefined || vaultId === null) return;
    if (!window.confirm('Discard this recording? It will not be kept or sent.')) return;
    setPhase('sealing');
    try {
      await discardOwnerDraft(api, vaultId, messageId);
      if (cekRef.current !== null) {
        cekRef.current.fill(0);
        cekRef.current = null;
      }
      navigate('/recordings', { replace: true });
    } catch {
      setError('We could not discard this recording. Please try again.');
      setPhase('ready');
    }
  }, [api, vaultId, messageId, navigate]);

  if (phase === 'loading') {
    return (
      <section className="sched">
        <p className="sched__kicker">Review</p>
        <h1 className="sched__title">Opening this recording…</h1>
        <p className="sched__muted" role="status">
          Unlocking it with your device key. Only you can open what was recorded for you.
        </p>
      </section>
    );
  }

  if (phase === 'error') {
    return (
      <section className="sched">
        <p className="sched__kicker">Review</p>
        <h1 className="sched__title">This recording could not be opened.</h1>
        <p className="sched__alert" role="alert">{error}</p>
        <button className="la-btn-secondary" onClick={() => navigate('/recordings')}>
          Back to recordings
        </button>
      </section>
    );
  }

  if (blob === null) return null;

  return (
    <>
      {phase === 'sealing' && (
        <p className="sched__muted" role="status">Sealing this recording…</p>
      )}
      {sealedRecipientName !== null && (
        <div className="sched" style={{ paddingBottom: 0 }}>
          <p className="guard__notice" role="status">
            This recording is now sealed for <strong>{sealedRecipientName}</strong> and can only go to
            them — you’re just choosing when it arrives. To send it to someone else instead, discard it and
            start again.
          </p>
        </div>
      )}
      {error !== null && (
        <div className="sched" style={{ paddingBottom: 0 }}>
          <p className="sched__alert" role="alert">{error}</p>
        </div>
      )}
      <ScheduleView
        key={sealedRecipientName ?? 'unsealed'}
        blob={blob}
        onBack={() => navigate('/recordings')}
        onScheduled={(d) => void onScheduled(d)}
        backLabel="Back"
        heading={{ kicker: 'Review', title: sealedRecipientName !== null ? 'When should it reach them?' : 'Who is this for?' }}
        allowCompound={false}
        lockedRecipientId={sealedRecipientName !== null ? (sealedRecipientIdRef.current ?? undefined) : undefined}
      />
      <div className="sched__actions" style={{ marginTop: '1rem' }}>
        <button type="button" className="la-btn-secondary" disabled={phase === 'sealing'} onClick={() => void onDiscard()}>
          Discard this recording
        </button>
      </div>
    </>
  );
}
